import React, { useEffect, useState } from "react";
import Slider from "rc-slider";
import BottomNavigation from "./BrandCampaignLayout/BottomNavigation";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "../../../hooks/useToast";
import { getMaxViews } from "../../../store/campaignStore/actions/CreateCampaignActions/getMaxViewsActions";
import { getBudget } from "../../../store/campaignStore/actions/CreateCampaignActions/getBudgetActions";
import {
  setBrandCampaignCreationData,
  setBrandCampaignCreationStep,
} from "../../../store/publicStore/actions/utilsActions/utilsActions";
import { getBrandCampaign } from "../../../store/campaignStore/actions/CreateCampaignActions/getBrandCampaignActions";
import { createCampaign } from "../../../store/campaignStore/actions/CreateCampaignActions/createCampaignActions";
import "rc-slider/assets/index.css";
import "./AudienceDemographicDetails.css";
import {
  getUserInfo,
  getUserInfoReset,
} from "../../../store/authStore/actions/authActions/getUserInfoActions";
import Popup from "../../popups/Popup/Popup";
import { BarndImg } from "../../../static/constant/imgConstants";
import { addMoneyToWallet } from "../../../store/influencerCampaignStore/actions/InfluencerCampaignActions/addMoneyToWalletActions";
import { payment_redirect_urii, payment_redirect_urii_agency } from "../../../store/constants";
import { txnStatus } from "../../../store/influencerCampaignStore/actions/InfluencerCampaignActions/txnStatusActions";
import { FaCircleCheck } from "react-icons/fa6";
import { createCampaignByAgency } from "../../../store/campaignStore/actions/CreateCampaignActions/createCampaignByAgencyActions";
import { uploadReferenceVideo } from "../../../store/campaignStore/actions/CreateCampaignActions/uploadReferenceVideoActions";

const { Handle, SliderTooltip } = Slider;

const TooltipHandle = (props) => {
  const { value, dragging, index, ...restProps } = props;
  return (
    <SliderTooltip
      prefixCls="rc-slider-tooltip"
      overlay={`${value} views`}
      visible={dragging}
      placement="top"
      key={index}
    >
      <Handle {...restProps} />
    </SliderTooltip>
  );
};

// Modal component
const ConfirmationModal = ({ onConfirm, onCancel, budget, loading }) => (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
    <div className="bg-white p-[3.2rem] rounded-lg shadow-lg max-w-[60rem] w-full">
      <h3 className="text-[3.2rem] font-semibold mb-[1rem] text-[#0D9B50]">
        Confirmation!
      </h3>
      <p className="text-[2rem]">
        Are you sure you want to use ${budget} from your wallet for this
        campaign?
      </p>
      <div className="mt-6 flex justify-end gap-4 text-[1.6rem]">
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 bg-green-500 text-white rounded disabled:bg-gray-400 flex items-center gap-2"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              Confirming...
            </>
          ) : (
            "Confirm"
          )}
        </button>
      </div>
    </div>
  </div>
);

const EstimatedBudget = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const Toast = useToast();
  const divideValue = 200;

  // Helper function to map campaign type for backend APIs
  const getSanitizedCampaignType = (campaignType) => {
    if (campaignType === "User Generated Content") return "UGC";
    if (campaignType === "Brand Generated Content") return "BGC";
    return campaignType?.toUpperCase();
  };

  const { brandCampaignCreationData } = useSelector(
    (state) => state.utilReducers
  );
  const getBudgetReducerData = useSelector(
    (state) => state.getBudgetReducer?.data
  );
  const getBudgetReducerMessage = useSelector(
    (state) => state.getBudgetReducer?.message
  );
  const getMaxViewsReducerData = useSelector(
    (state) => state.getMaxViewsReducer?.data
  );
  const getUserInfoReducer = useSelector((state) => state.getUserInfoReducer);
  const getUserInfoReducerData = useSelector(
    (state) => state.getUserInfoReducer?.data
  );
  const createCampaignLoading = useSelector(
    (state) => state.createCampaignReducer?.loading
  );
  const createCampaignByAgencyLoading = useSelector(
    (state) => state.createCampaignByAgencyReducer?.loading
  );

  const isCreating = createCampaignLoading || createCampaignByAgencyLoading;

  // For UGC campaigns the backend also returns a content-creation fee estimate
  // (estimatedContentCreationBudget) alongside the CPV-based estimatedBudget,
  // and totalEstimatedBudget = the sum of both. This is the real number that
  // must be checked against wallet balance and reserved as the campaign's
  // budget - CPV-only would under-fund campaigns that also pay a content fee.
  const effectiveEstimatedBudget =
    getBudgetReducerData?.totalEstimatedBudget ?? getBudgetReducerData?.estimatedBudget;

  const [expectedViews, setExpectedViews] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [selectedInfluencer, setSelectedInfluencer] = useState("Nano");
  const [isModalOpen, setIsModalOpen] = useState(false); // For modal visibility
  const [isChecked, setIsChecked] = useState(false);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [isPaymentProceedPopupOpen, setIsPaymentProceedPopupOpen] =
    useState(false);
  const [isWaletBudgetChecked, setIsWaletBudgetChecked] = useState(false);
  const [enteredAmount, setEnteredAmount] = useState(0);
  const [isSuccessPopupOpen, setSuccessPopupOpen] = useState(false);

  // Define an array of influencer types
  const influencerTypes = ["Nano", "Micro", "Medium", "Large"];

  const anchorLink = getUserInfoReducer?.data?.userType === 'AGENCY' ? "https://terms.paidpromo.ai/agency_campaign_terms.html" : "https://terms.paidpromo.ai/brand_campaign_terms.html"

  useEffect(() => {
    // Reset the picker to zero whenever the reachable pool changes; brand
    // chooses their spend from 0 up to the estimated maximum.
    setExpectedViews(0);
  }, [getMaxViewsReducerData]);

  // Fetch max views whenever inputs or influencer type change
  useEffect(() => {
    dispatch(
      getMaxViews({
        platform: brandCampaignCreationData?.basicDetails?.platform,
        campaignType: getSanitizedCampaignType(
          brandCampaignCreationData?.basicDetails?.campaignType
        ),
        regionId: Number(
          brandCampaignCreationData?.audienceDemographicDetails?.region
        ),
        countryId: Number(
          brandCampaignCreationData?.audienceDemographicDetails?.country
        ),
        stateId: Number(
          brandCampaignCreationData?.audienceDemographicDetails?.state
        ),
        gender: brandCampaignCreationData?.audienceDemographicDetails?.gender,
        nicheId: brandCampaignCreationData?.audienceDemographicDetails?.niche || [],
        age: brandCampaignCreationData?.audienceDemographicDetails?.ageGroup,
        influencerType: selectedInfluencer?.toUpperCase(),
        cityId: brandCampaignCreationData?.basicDetails?.platform === 'INSTAGRAM' && brandCampaignCreationData?.audienceDemographicDetails?.city
          ? Number(brandCampaignCreationData?.audienceDemographicDetails?.city)
          : null,
      })
    );
  }, [brandCampaignCreationData, selectedInfluencer]);

  // Fetch budget whenever expectedViews or influencer type changes
  useEffect(() => {
    dispatch(
      getBudget({
        platform: brandCampaignCreationData?.basicDetails?.platform,
        campaignType: getSanitizedCampaignType(
          brandCampaignCreationData?.basicDetails?.campaignType
        ),
        regionId: Number(
          brandCampaignCreationData?.audienceDemographicDetails?.region
        ),
        countryId: Number(
          brandCampaignCreationData?.audienceDemographicDetails?.country
        ),
        stateId: Number(
          brandCampaignCreationData?.audienceDemographicDetails?.state
        ),
        gender: brandCampaignCreationData?.audienceDemographicDetails?.gender,
        nicheId: brandCampaignCreationData?.audienceDemographicDetails?.niche || [],
        age: brandCampaignCreationData?.audienceDemographicDetails?.ageGroup,
        selectedViews: Number(expectedViews),
        rateCardId: Number(getBudgetReducerData?.rateCardId),
        influencerType: selectedInfluencer.toUpperCase(),
        contentDurationSeconds: brandCampaignCreationData?.contentDetails?.contentDurationSeconds || null,
        cityId: brandCampaignCreationData?.basicDetails?.platform === 'INSTAGRAM' && brandCampaignCreationData?.audienceDemographicDetails?.city
          ? Number(brandCampaignCreationData?.audienceDemographicDetails?.city)
          : null,
      })
    );
  }, [expectedViews, selectedInfluencer]);

  function formatDateTime(input) {
    const date = new Date(input);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = "50";
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const handleCleanUp = () => {
    dispatch(
      setBrandCampaignCreationData({
        basicDetails: {
          campaignName: null,
          campaignPurpose: null,
          platform: null,
          campaignType: null,
          referralCode: null,
          selectedBrandId: null,
        },
        contentDetails: {
          startDate: null,
          endDate: null,
          bannerTemplate: {},
          selectedBannerName: null,
          videoPosition: null,
          mediaFileLink: null,
          mediaFileName: null,
          script: null,
          dialogue: null,
          backgroundColor: null,
          otherProperties: null,
          isLocationRequired: null,
          isBrandApprovalRequired: null,
          uploadDuration: null,
        },
        audienceDemographicDetails: {
          ageGroup: null,
          country: null,
          niche: null,
          region: null,
          gender: null,
          state: null,
          city: null,
        },
      })
    );
    dispatch(setBrandCampaignCreationStep(1));
    dispatch(getBrandCampaign());
    navigate(getUserInfoReducerData?.userType === 'AGENCY' ? "/agency/campaigns" : "/brand/campaigns");
  };

  const handleNext = () => {
    setIsWaletBudgetChecked(true);
    if (
      getUserInfoReducer?.data?.walletBalance <
      effectiveEstimatedBudget
    ) {
      setIsPaymentProceedPopupOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  console.log("brandCampaignCreationData", brandCampaignCreationData)

  const confirmAndProceed = () => {
    console.log("getUserInfoReducer?.data?.userType === 'AGENCY'", getUserInfoReducer?.data?.userType === 'AGENCY')
    console.log("brandCampaignCreationData", brandCampaignCreationData)
    if (getUserInfoReducer?.data?.userType === 'AGENCY') {
      dispatch(
        createCampaignByAgency(
          {
            campaignName: brandCampaignCreationData?.basicDetails?.campaignName,
            purpose: brandCampaignCreationData?.basicDetails?.campaignPurpose,
            platform: brandCampaignCreationData?.basicDetails?.platform,
            campaignType: getSanitizedCampaignType(
              brandCampaignCreationData?.basicDetails?.campaignType
            ),
            startDate: brandCampaignCreationData?.contentDetails?.startDate,
            endDate: brandCampaignCreationData?.contentDetails?.endDate,
            bannerTemplate:
              brandCampaignCreationData?.contentDetails?.bannerTemplate,
            videoPosition:
              brandCampaignCreationData?.contentDetails?.videoPosition,
            mediaFileLink:
              brandCampaignCreationData?.contentDetails?.mediaFileLink,
            script: brandCampaignCreationData?.contentDetails?.script,
            dialogue: brandCampaignCreationData?.contentDetails?.dialogue,
            backgroundColor:
              brandCampaignCreationData?.contentDetails?.backgroundColor,
            otherProperties:
              brandCampaignCreationData?.contentDetails?.otherProperties,
            isLocationRequired:
              brandCampaignCreationData?.contentDetails?.isLocationRequired,
            isBrandApprovalRequired:
              brandCampaignCreationData?.contentDetails?.isBrandApprovalRequired,
            uploadDuration: brandCampaignCreationData?.contentDetails?.uploadDuration ? formatDateTime(
              brandCampaignCreationData?.contentDetails?.uploadDuration
            ) : null,
            contentDurationSeconds: brandCampaignCreationData?.contentDetails?.contentDurationSeconds || null,
            title: brandCampaignCreationData?.contentDetails?.videoTitle,
            description: brandCampaignCreationData?.contentDetails?.videoDescription,
            regionId: Number(
              brandCampaignCreationData?.audienceDemographicDetails?.region
            ),
            countryId: Number(
              brandCampaignCreationData?.audienceDemographicDetails?.country
            ),
            stateId: Number(
              brandCampaignCreationData?.audienceDemographicDetails?.state
            ),
            cityId: brandCampaignCreationData?.basicDetails?.platform === 'INSTAGRAM' && brandCampaignCreationData?.audienceDemographicDetails?.city
              ? Number(brandCampaignCreationData?.audienceDemographicDetails?.city)
              : null,
            campaignAgeGroups:
              brandCampaignCreationData?.audienceDemographicDetails?.ageGroup,
            gender: brandCampaignCreationData?.audienceDemographicDetails?.gender,
            niches: brandCampaignCreationData?.audienceDemographicDetails?.niche || [],
            influencerType: selectedInfluencer.toUpperCase(),
            targetView: Number(expectedViews),
            estimatedBudget: Number(effectiveEstimatedBudget),
            referralCode: brandCampaignCreationData?.basicDetails.referralCode,
            brandId: brandCampaignCreationData?.basicDetails.selectedBrandId,
            referenceVideoUpload: brandCampaignCreationData?.contentDetails?.referenceVideoUpload,
            descriptionLink: brandCampaignCreationData?.contentDetails?.descriptionLink,
          },
          (response) => {
            // Toast(getBudgetReducerMessage, 'success');
            console.log("create campaign response : ", response);
            if (response && response?.status === 200) {
              setIsModalOpen(false); // Close modal
              Toast("Campaign Created Successfully", "success");
              handleCleanUp();
              dispatch(getUserInfo());
            } else {
              setIsModalOpen(false);
              Toast(response.message, "error");
            }
          }
        )
      );
    } else {
      dispatch(
        createCampaign(
          {
            campaignName: brandCampaignCreationData?.basicDetails?.campaignName,
            purpose: brandCampaignCreationData?.basicDetails?.campaignPurpose,
            platform: brandCampaignCreationData?.basicDetails?.platform,
            campaignType: getSanitizedCampaignType(
              brandCampaignCreationData?.basicDetails?.campaignType
            ),
            startDate: brandCampaignCreationData?.contentDetails?.startDate,
            endDate: brandCampaignCreationData?.contentDetails?.endDate,
            bannerTemplate:
              brandCampaignCreationData?.contentDetails?.bannerTemplate,
            videoPosition:
              brandCampaignCreationData?.contentDetails?.videoPosition,
            mediaFileLink:
              brandCampaignCreationData?.contentDetails?.mediaFileLink,
            script: brandCampaignCreationData?.contentDetails?.script,
            dialogue: brandCampaignCreationData?.contentDetails?.dialogue,
            backgroundColor:
              brandCampaignCreationData?.contentDetails?.backgroundColor,
            otherProperties:
              brandCampaignCreationData?.contentDetails?.otherProperties,
            isLocationRequired:
              brandCampaignCreationData?.contentDetails?.isLocationRequired,
            isBrandApprovalRequired:
              brandCampaignCreationData?.contentDetails?.isBrandApprovalRequired,
            uploadDuration: brandCampaignCreationData?.contentDetails?.uploadDuration ? formatDateTime(
              brandCampaignCreationData?.contentDetails?.uploadDuration
            ) : null,
            contentDurationSeconds: brandCampaignCreationData?.contentDetails?.contentDurationSeconds || null,
            title: brandCampaignCreationData?.contentDetails?.videoTitle,
            description: brandCampaignCreationData?.contentDetails?.videoDescription,
            regionId: Number(
              brandCampaignCreationData?.audienceDemographicDetails?.region
            ),
            countryId: Number(
              brandCampaignCreationData?.audienceDemographicDetails?.country
            ),
            stateId: Number(
              brandCampaignCreationData?.audienceDemographicDetails?.state
            ),
            cityId: brandCampaignCreationData?.basicDetails?.platform === 'INSTAGRAM' && brandCampaignCreationData?.audienceDemographicDetails?.city
              ? Number(brandCampaignCreationData?.audienceDemographicDetails?.city)
              : null,
            campaignAgeGroups:
              brandCampaignCreationData?.audienceDemographicDetails?.ageGroup,
            gender: brandCampaignCreationData?.audienceDemographicDetails?.gender,
            niches: brandCampaignCreationData?.audienceDemographicDetails?.niche || [],
            influencerType: selectedInfluencer.toUpperCase(),
            targetView: Number(expectedViews),
            estimatedBudget: Number(effectiveEstimatedBudget),
            referralCode: brandCampaignCreationData?.basicDetails.referralCode,
            referenceVideoUpload: brandCampaignCreationData?.contentDetails?.referenceVideoUpload || null,
            descriptionLink: brandCampaignCreationData?.contentDetails?.descriptionLink || null,
          },
          (response) => {
            // Toast(getBudgetReducerMessage, 'success');
            console.log("create campaign response : ", response);
            if (response && response?.status === 200) {
              setIsModalOpen(false); // Close modal
              Toast("Campaign Created Successfully", "success");
              handleCleanUp();
              dispatch(getUserInfo());
            } else {
              setIsModalOpen(false);
              Toast(response.message, "error");
            }
          }
        )
      );
    }
  };

  const formatThousand = (num) => {
    if (!num) return "0";
    return num >= 1000 ? (num / 1000).toFixed(1) + "K" : num;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionIdFromUrl = params.get("sessionId");
    const paymentStatusFromUrl = params.get("paymentStatus");

    if (!sessionIdFromUrl) return; // Avoid unnecessary execution

    setSuccessPopupOpen(true);
    console.log("Payment Status:", paymentStatusFromUrl);

    if (paymentStatusFromUrl === "success") {
      console.log("✅ Payment was successful!");
    } else if (paymentStatusFromUrl === "failed") {
      console.log("❌ Payment failed or was canceled.");
    }

    // Dispatch transaction status update
    dispatch(
      txnStatus(
        {
          sessionId: sessionIdFromUrl,
          status: paymentStatusFromUrl,
        },
        (status, response, message) => {
          if (status === 200) {
            if (window.opener) {
              window.opener.postMessage({ paymentCompleted: true }, "*");
              window.close();
            }
          } else {
            console.error("Failed to fetch transaction status:", message);
          }
        }
      )
    );
  }, [location.search, dispatch, navigate]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.paymentCompleted) {
        console.log("Payment completed, updating user info");
        dispatch(getUserInfo());
        setSuccessPopupOpen(true);
        // Remove the listener after successful dispatch
        window.removeEventListener("message", handleMessage);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [dispatch]);

  console.log("selectedInfluencer", selectedInfluencer)

  return (
    <div className="bg-[#FFFFFF66] shadow-xl shadow-[#E3E8EF] p-8 rounded-lg w-full h-[80%]">
      <div className="w-full h-full !overflow-y-scroll no-scrollbar">
        <h2 className="text-[1.6rem] text-[#111729] inter-font font-[400] mb-4">
          Influencer Type
        </h2>
        <div className="flex gap-4 mb-[2.2rem]">
          {influencerTypes.map((type) => (
            <label
              key={type}
              className="flex items-center text-[1.6rem] text-[#111729] inter-font font-[400] mr-[3.2rem]"
            >
              <input
                type="radio"
                name="INFLUENCER"
                value={type}
                checked={selectedInfluencer === type}
                onChange={(event) => {
                  setSelectedInfluencer(event.target.value);
                }}
                className="mr-2"
              />
              {type}
              {selectedInfluencer === type && (
                <span className="ml-2 text-gray-500 text-[1.4rem]">
                  ({getMaxViewsReducerData?.numberOfChannels || 0})
                </span>
              )}
            </label>
          ))}
        </div>

        {selectedInfluencer && (
          <div className="text-[1.6rem] text-[#111729] inter-font font-[500] mb-6">
            No. of influencers available : {getMaxViewsReducerData?.numberOfChannels || 0}
          </div>
        )}

        <div className="mb-[2.2rem]">
          <label className="text-[1.6rem] text-[#111729] inter-font font-[400]">
            What's your expected views for this campaign?
          </label>
          <div className="w-full h-[5rem] flex justify-center items-center">
            <div className="w-[2.7rem] h-[2.7rem] flex justify-center items-center rounded-full bg-[#43AB4E]"></div>
            <Slider
              min={0}
              max={getMaxViewsReducerData?.estimatedViews || divideValue}
              step={divideValue}
              value={expectedViews ?? 0}
              onChange={(value) => setExpectedViews(value)}
              trackStyle={[{ backgroundColor: "#22c55e" }]}
              handleStyle={[{ backgroundColor: "#43AB4E" }]}
              railStyle={{ backgroundColor: "#B8B0B0" }}
              handle={TooltipHandle}
            />

            <div className="w-[2.7rem] h-[2.7rem] flex justify-center items-center rounded-full bg-[#EEf0F1]"></div>
          </div>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-[1.6rem] text-[#111729] inter-font font-[500]">
              {formatThousand(0)}
            </span>
            <span className="text-[1.6rem] text-[#111729] inter-font font-[500]">
              {formatThousand(expectedViews)}
            </span>
            <span className="text-[1.6rem] text-[#111729] inter-font font-[500]">{`${formatThousand(
              getMaxViewsReducerData?.estimatedViews
            )}`}</span>
          </div>
        </div>

        {getBudgetReducerData?.estimatedContentCreationBudget != null ? (
          <div className="w-[40rem] sm:w-[60.4rem] 2xl:w-[80rem] mb-[1.2rem]">
            <h3 className="text-[1.6rem] text-[#111729] inter-font font-[400] mb-2">
              Estimated Budget
            </h3>
            <div className="w-full border border-gray-300 rounded-lg px-4 py-4 text-[1.6rem] text-[#111729] inter-font font-[400]">
              <div className="flex justify-between py-1">
                <span>Pay-per-view budget</span>
                <span>$ {getBudgetReducerData?.estimatedBudget}</span>
              </div>
              <div className="flex justify-between py-1">
                <span>Content creation fee (paid on approval)</span>
                <span>$ {getBudgetReducerData?.estimatedContentCreationBudget}</span>
              </div>
              <div className="flex justify-between py-2 mt-1 border-t border-gray-300 font-[600]">
                <span>Total</span>
                <span>$ {getBudgetReducerData?.totalEstimatedBudget}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-[40rem] sm:w-[60.4rem] 2xl:w-[80rem] mb-[1.2rem]">
            <h3 className="text-[1.6rem] text-[#111729] inter-font font-[400] mb-2">
              Estimated Budget
            </h3>
            <h3 className="w-full border border-gray-300 text-[1.6rem] text-[#111729] inter-font font-[400] rounded-lg px-4 py-4 focus:outline-none">
              $ {getBudgetReducerData?.estimatedBudget}
            </h3>
          </div>
        )}

        <div className="flex items-center text-[1.4rem] text-[#111729] inter-font font-[400]">
          <input
            type="checkbox"
            id="termsCheckbox"
            className="w-[1.4rem] h-[1.4rem] sm:w-[1.6rem] sm:h-[1.6rem]"
            checked={isChecked}
            onChange={() => setIsChecked(!isChecked)}
          />
          <label htmlFor="termsCheckbox" className="ml-3">
            I agree to the{" "}
            <a
              href={anchorLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#007BFF] underline"
            >
              Terms & Conditions
            </a>
          </label>
        </div>

        <BottomNavigation
          isNextDisabled={
            Object.keys(formErrors).length > 0 ||
            !getMaxViewsReducerData ||
            !isChecked ||
            !effectiveEstimatedBudget
          }
          handleNext={handleNext}
        />

        {isModalOpen && (
          <ConfirmationModal
            budget={effectiveEstimatedBudget}
            onConfirm={confirmAndProceed}
            onCancel={() => setIsModalOpen(false)}
            loading={isCreating}
          />
        )}

        <Popup
          isOpen={isPopupOpen}
          onClose={() => setPopupOpen(false)}
          title="Add Money Into Wallet"
          canDismiss={false}
          icon={
            <img
              src={BarndImg.wallet}
              alt=""
              className="w-[5.1rem] h-[5.1rem] my-[2rem]"
            />
          }
          content={
            <div className="">
              <label className="text-[#111729] text-[1.4rem] mb-4">
                Enter Digits
              </label>
              <input
                type="text"
                className="w-full px-4 py-4 text-[1.4rem] border border-gray-300 rounded-lg focus:outline-none "
                value={enteredAmount}
                maxLength={6}
                onChange={(e) => {
                  const value = e.target.value;
                  let numericValue = value.replace(/[^0-9]/g, "");
                  numericValue = numericValue.replace(/^0+/, '');
                  setEnteredAmount(numericValue === '' ? '0' : numericValue);
                }}
              />
            </div>
          }
          size="sm"
          primaryButton={{
            text: "Add Money",
            onClick: () => {
              if (enteredAmount == 0) {
                Toast("Please enter Amount");
              } else if (enteredAmount > 999999) {
                Toast("Amount should be less than 999999");
              } else {
                dispatch(
                  addMoneyToWallet(
                    {
                      amount: enteredAmount,
                      successUrl: getUserInfoReducer?.data?.userType === 'AGENCY' ? `${payment_redirect_urii_agency}success` : `${payment_redirect_urii}success`,
                      cancelUrl: getUserInfoReducer?.data?.userType === 'AGENCY' ? `${payment_redirect_urii_agency}failed` : `${payment_redirect_urii}failed`,
                    },
                    (status, response, message) => {
                      if (status === 200) {
                        // window.open(response?.url, "_blank");
                        // window.open(response?.url, "_blank", "width=800,height=600,noopener,noreferrer");
                        window.open(
                          response?.url,
                          "_blank",
                          "width=800,height=600,resizable,scrollbars,status"
                        );
                      } else if (status) {
                        Toast(message, "error");
                      }
                    }
                  )
                );
                setEnteredAmount(0);
                setPopupOpen(false);
              }
            },
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => {
              setEnteredAmount(0);
              setPopupOpen(false);
            },
          }}
        />

        <Popup
          isOpen={isPaymentProceedPopupOpen}
          onClose={() => setIsPaymentProceedPopupOpen(false)}
          canDismiss={false}
          content={
            <div className="">
              <h3 className="text-[3rem] font-semibold mb-[1rem] text-red-500">
                Insufficient Balance!
              </h3>
              <p className="text-[2rem]">
                You do not have enough balance in your wallet to continue with the
                payment.
              </p>
            </div>
          }
          size="sm"
          primaryButton={{
            text: "Add Money",
            onClick: () => {
              setIsPaymentProceedPopupOpen(false);
              setPopupOpen(true);
            },
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => {
              setIsPaymentProceedPopupOpen(false);
            },
          }}
        />

        <Popup
          isOpen={isSuccessPopupOpen}
          onClose={() => setSuccessPopupOpen(false)}
          title="Success"
          canDismiss
          icon={
            <FaCircleCheck color="#0D9B50" size={110} className="my-[5rem]" />
          }
          content={
            <p className="text-[1.6rem] w-[80%] mx-auto text-center">
              Payment Successfull
            </p>
          }
          size="sm"
          primaryButton={{
            text: "Done",
            onClick: () => {
              setSuccessPopupOpen(false);
            },
          }}
        />
      </div>
    </div >
  );
};

export default EstimatedBudget;
